import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BoutiqueAdminDTO, GroupDTO, HiboutikAccountDTO, ShopType } from "@easyshop/shared";
import { SHOP_TYPE_LABELS } from "@easyshop/shared";
import {
  Button,
  Card,
  Drawer,
  DrawerHeader,
  Empty,
  ErrorState,
  FieldLabel,
  Loading,
  Pill,
  SectionTitle,
  TypeBadge,
  cx,
  inputClass,
} from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { api } from "@/lib/api";
import { useAdminBoutiques, useGroups, useHiboutikAccounts, useItApiKeys, useItSettings, useNetworkBranding } from "@/lib/queries";
import { NetworkLogo } from "@/components/NetworkLogo";
import { eur, relative } from "@/lib/format";

interface ConnectionResult {
  ok: boolean;
  authOk?: boolean;
  storeId?: number | null;
  salesToday?: number | null;
  message: string;
}

interface StoresResponse {
  stores: { id: number; name: string }[];
  usedShopIds: number[];
}

function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: string[]) => {
    for (const k of keys) void qc.invalidateQueries({ queryKey: [k] });
  };
}

function ResultBox({ r }: { r: ConnectionResult }) {
  return (
    <div
      className={cx(
        "rounded-card-sm px-4 py-3 text-[12.5px] font-semibold leading-[1.45]",
        r.ok ? "bg-positive-bg text-positive" : "bg-danger-bg text-danger",
      )}
    >
      {r.ok ? "✓ " : "✕ "}
      {r.message}
    </div>
  );
}

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      {children}
      {hint && <div className="mt-[6px] text-[11.5px] text-muted-2">{hint}</div>}
    </div>
  );
}

function ConnectionPill({ ok, at }: { ok: boolean | null; at: string | null }) {
  if (ok == null) return <Pill className="bg-page text-muted-2">Jamais testé</Pill>;
  return (
    <Pill className={ok ? "bg-positive-bg text-positive" : "bg-danger-bg text-danger"}>
      {ok ? "Connexion OK" : "Échec de connexion"}
      {at ? ` · ${relative(at)}` : ""}
    </Pill>
  );
}

// --- Comptes Hiboutik ------------------------------------------------------------

function AccountDrawer({ account, onClose }: { account: HiboutikAccountDTO | null; onClose: () => void }) {
  const invalidate = useInvalidate();
  const [form, setForm] = useState({
    label: account?.label ?? "",
    subdomain: account?.subdomain ?? "",
    apiUser: account?.apiUser ?? "",
    apiKey: "",
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const test = useMutation({
    mutationFn: () =>
      account && !form.apiKey
        ? api.post<ConnectionResult>(`/hiboutik-accounts/${account.id}/test`)
        : api.post<ConnectionResult>("/hiboutik-accounts/test", {
            subdomain: form.subdomain.trim(),
            apiUser: form.apiUser.trim(),
            apiKey: form.apiKey.trim(),
          }),
    onSuccess: () => account && invalidate("hiboutik-accounts"),
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: form.label.trim(),
        subdomain: form.subdomain.trim(),
        apiUser: form.apiUser.trim(),
        ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
      };
      return account ? api.patch(`/hiboutik-accounts/${account.id}`, body) : api.post("/hiboutik-accounts", body);
    },
    onSuccess: () => {
      invalidate("hiboutik-accounts", "admin-boutiques", "dashboard", "me");
      onClose();
    },
  });
  const canTest = account ? true : Boolean(form.subdomain.trim() && form.apiUser.trim() && form.apiKey.trim());

  return (
    <Drawer open onClose={onClose} width={440}>
      <DrawerHeader title={account ? `Modifier « ${account.label} »` : "Ajouter un compte Hiboutik"} onClose={onClose} />
      <div className="text-[12.5px] leading-[1.5] text-muted">
        Les identifiants API se trouvent dans Hiboutik : <b>Paramètres → API</b>. La clé est chiffrée et n'est plus jamais affichée en
        entier.
      </div>
      <Field label="Nom du compte">
        <input className={inputClass} placeholder="ex. Rhône-Loire" value={form.label} onChange={set("label")} />
      </Field>
      <Field label="Sous-domaine">
        <div className="flex items-center gap-2">
          <input className={inputClass} placeholder="maboutique" value={form.subdomain} onChange={set("subdomain")} />
          <span className="flex-shrink-0 text-[13px] text-muted">.hiboutik.com</span>
        </div>
      </Field>
      <Field label="Utilisateur API">
        <input
          className={inputClass}
          placeholder="e-mail ou identifiant API"
          value={form.apiUser}
          onChange={set("apiUser")}
          autoComplete="off"
        />
      </Field>
      <Field
        label={account ? "Nouvelle clé API" : "Clé API"}
        hint={account ? `Actuelle : ${account.apiKeyPreview} — laisser vide pour la conserver.` : undefined}
      >
        <input className={inputClass} type="password" autoComplete="new-password" value={form.apiKey} onChange={set("apiKey")} />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => test.mutate()} disabled={!canTest || test.isPending}>
          {test.isPending ? "Test en cours…" : "Tester la connexion"}
        </Button>
        {account && !form.apiKey && <span className="text-[11.5px] text-muted-2">avec les identifiants enregistrés</span>}
      </div>
      {test.data && <ResultBox r={test.data} />}
      {test.error && <ErrorState error={test.error} />}
      {save.error && <ErrorState error={save.error} />}
      <div className="mt-auto flex gap-2 pt-2">
        <Button
          onClick={() => save.mutate()}
          disabled={
            save.isPending || !form.label.trim() || !form.subdomain.trim() || !form.apiUser.trim() || (!account && !form.apiKey.trim())
          }
        >
          {save.isPending ? "Enregistrement…" : "Enregistrer"}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Annuler
        </Button>
      </div>
    </Drawer>
  );
}

function AccountCard({ a, onEdit }: { a: HiboutikAccountDTO; onEdit: () => void }) {
  const invalidate = useInvalidate();
  const test = useMutation({
    mutationFn: () => api.post<ConnectionResult>(`/hiboutik-accounts/${a.id}/test`),
    onSettled: () => invalidate("hiboutik-accounts"),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/hiboutik-accounts/${a.id}`),
    onSuccess: () => invalidate("hiboutik-accounts", "me"),
  });
  return (
    <Card pad="px-[22px] py-5" className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[16px] font-bold">{a.label}</div>
          <div className="truncate text-[12px] text-muted-2">{a.subdomain}.hiboutik.com</div>
        </div>
        <ConnectionPill ok={a.lastConnectionOk} at={a.lastConnectionAt} />
      </div>
      <div className="grid grid-cols-[110px_1fr] gap-y-[6px] text-[12.5px]">
        <span className="text-muted">Utilisateur API</span>
        <span className="truncate font-semibold">{a.apiUser}</span>
        <span className="text-muted">Clé API</span>
        <span className="font-mono text-[12px]">{a.apiKeyPreview}</span>
        <span className="text-muted">Boutiques</span>
        <span className="flex flex-wrap gap-1">
          {a.boutiques.length === 0 && <span className="text-muted-2">Aucune</span>}
          {a.boutiques.map((b) => (
            <Pill key={b.id} className="bg-page text-[oklch(0.4_0.02_150)]">
              {b.city}
            </Pill>
          ))}
        </span>
      </div>
      {test.data && <ResultBox r={test.data} />}
      {(test.error || del.error) && <ErrorState error={test.error ?? del.error} />}
      <div className="flex flex-wrap gap-2 border-t border-line-soft pt-3">
        <Button variant="outline" className="!px-[14px] !py-[7px] !text-[12px]" onClick={() => test.mutate()} disabled={test.isPending}>
          {test.isPending ? "Test…" : "Tester"}
        </Button>
        <Button variant="outline" className="!px-[14px] !py-[7px] !text-[12px]" onClick={onEdit}>
          Modifier
        </Button>
        <div className="flex-1" />
        <button
          type="button"
          disabled={a.boutiques.length > 0 || del.isPending}
          title={a.boutiques.length > 0 ? "Supprimez ou déplacez d'abord les boutiques de ce compte" : undefined}
          onClick={() => {
            if (window.confirm(`Supprimer le compte Hiboutik « ${a.label} » ?`)) del.mutate();
          }}
          className="rounded-full px-3 py-[7px] text-[12px] font-bold text-danger hover:bg-danger-bg disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Supprimer
        </button>
      </div>
    </Card>
  );
}

// --- Boutiques ------------------------------------------------------------------------

interface BoutiqueForm {
  name: string;
  city: string;
  type: ShopType;
  hiboutikAccountId: string;
  shopId: string;
  warehouseId: string;
  openingHours: string;
  monthlyRevenueTarget: string;
  minStaff: string;
  reportEmails: string;
  position: string;
  active: boolean;
}

function ShopPicker({
  accountId,
  value,
  onChange,
  currentShopId,
}: {
  accountId: string;
  value: string;
  onChange: (v: string) => void;
  currentShopId: number | null;
}) {
  const q = useQuery({
    queryKey: ["hiboutik-stores", accountId],
    queryFn: () => api.get<StoresResponse>(`/hiboutik-accounts/${accountId}/stores`),
    enabled: Boolean(accountId),
    retry: false,
    staleTime: 60_000,
  });
  const manual = (
    <input
      className={inputClass}
      type="number"
      min={1}
      placeholder="ex. 1 (vide = magasin par défaut)"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
  if (!accountId) return <div className="text-[12px] text-muted-2">Choisissez d'abord un compte Hiboutik.</div>;
  if (q.isLoading) return <div className="text-[12px] text-muted">Lecture des magasins Hiboutik…</div>;
  if (q.error || !q.data) {
    return (
      <>
        {manual}
        <div className="mt-[6px] text-[11.5px] font-semibold text-warn">
          Magasins indisponibles ({q.error instanceof Error ? q.error.message : "erreur"}) : saisissez le numéro de magasin.
        </div>
      </>
    );
  }
  const used = new Set(q.data.usedShopIds.filter((id) => id !== currentShopId));
  const known = q.data.stores.some((s) => String(s.id) === value);
  return (
    <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— Magasin par défaut du compte —</option>
      {q.data.stores.map((s) => (
        <option key={s.id} value={String(s.id)}>
          #{s.id} · {s.name}
          {used.has(s.id) ? " (déjà utilisé)" : ""}
        </option>
      ))}
      {value && !known && <option value={value}>#{value} (inconnu)</option>}
    </select>
  );
}

function BoutiqueDrawer({
  boutique,
  accounts,
  onClose,
}: {
  boutique: BoutiqueAdminDTO | null;
  accounts: HiboutikAccountDTO[];
  onClose: () => void;
}) {
  const invalidate = useInvalidate();
  const [f, setF] = useState<BoutiqueForm>({
    name: boutique?.name ?? "",
    city: boutique?.city ?? "",
    type: boutique?.type ?? "FRANCHISE",
    hiboutikAccountId: boutique?.hiboutikAccountId ?? accounts[0]?.id ?? "",
    shopId: boutique?.shopId != null ? String(boutique.shopId) : "",
    warehouseId: String(boutique?.warehouseId ?? 1),
    openingHours: boutique?.openingHours ?? "",
    monthlyRevenueTarget: boutique?.monthlyRevenueTarget != null ? String(boutique.monthlyRevenueTarget) : "",
    minStaff: String(boutique?.minStaff ?? 1),
    reportEmails: boutique?.reportEmails ?? "",
    position: String(boutique?.position ?? 0),
    active: boutique?.active ?? true,
  });
  const set = (k: keyof BoutiqueForm) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name.trim(),
        city: f.city.trim(),
        type: f.type,
        hiboutikAccountId: f.hiboutikAccountId,
        shopId: f.shopId.trim() ? Number(f.shopId) : null,
        warehouseId: Number(f.warehouseId) || 1,
        openingHours: f.openingHours.trim() || null,
        monthlyRevenueTarget: f.monthlyRevenueTarget.trim() ? Number(f.monthlyRevenueTarget.replace(",", ".")) : null,
        minStaff: Number(f.minStaff) || 0,
        reportEmails: f.reportEmails.trim() || null,
        position: Number(f.position) || 0,
        active: f.active,
      };
      return boutique ? api.patch(`/admin/boutiques/${boutique.id}`, body) : api.post("/admin/boutiques", body);
    },
    onSuccess: () => {
      invalidate("admin-boutiques", "hiboutik-accounts", "me", "dashboard", "groups");
      onClose();
    },
  });
  const del = useMutation({
    mutationFn: () => api.del(`/admin/boutiques/${boutique!.id}`),
    onSuccess: () => {
      invalidate("admin-boutiques", "hiboutik-accounts", "me", "dashboard", "groups");
      onClose();
    },
  });

  return (
    <Drawer open onClose={onClose} width={460}>
      <DrawerHeader title={boutique ? `Modifier ${boutique.city}` : "Ajouter une boutique"} onClose={onClose} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ville">
          <input className={inputClass} value={f.city} onChange={set("city")} placeholder="ex. Lyon" />
        </Field>
        <Field label="Nom">
          <input className={inputClass} value={f.name} onChange={set("name")} placeholder="ex. Lyon Part-Dieu" />
        </Field>
      </div>
      <Field label="Type">
        <div className="flex gap-2">
          {(["INTEGRATED", "FRANCHISE"] as ShopType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setF({ ...f, type: t })}
              className={cx(
                "flex-1 rounded-full border py-[9px] text-[12.5px] font-bold",
                f.type === t ? "border-accent-strong bg-accent-strong text-white" : "border-line bg-white text-muted",
              )}
            >
              {SHOP_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Compte Hiboutik">
        <select
          className={inputClass}
          value={f.hiboutikAccountId}
          onChange={(e) => setF({ ...f, hiboutikAccountId: e.target.value, shopId: "" })}
        >
          {accounts.length === 0 && <option value="">Aucun compte — ajoutez-en un d'abord</option>}
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.label} ({a.subdomain})
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-[1fr_120px] gap-3">
        <Field label="Magasin (shop)">
          <ShopPicker
            accountId={f.hiboutikAccountId}
            value={f.shopId}
            onChange={(v) => setF({ ...f, shopId: v })}
            currentShopId={boutique && boutique.hiboutikAccountId === f.hiboutikAccountId ? boutique.shopId : null}
          />
        </Field>
        <Field label="Entrepôt">
          <input className={inputClass} type="number" min={1} value={f.warehouseId} onChange={set("warehouseId")} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Objectif mensuel (€)">
          <input
            className={inputClass}
            inputMode="decimal"
            value={f.monthlyRevenueTarget}
            onChange={set("monthlyRevenueTarget")}
            placeholder="ex. 60000"
          />
        </Field>
        <Field label="Horaires">
          <input className={inputClass} value={f.openingHours} onChange={set("openingHours")} placeholder="ex. 9h30–19h" />
        </Field>
        <Field label="Effectif minimum (planning)">
          <input className={inputClass} type="number" min={0} max={30} value={f.minStaff} onChange={set("minStaff")} />
        </Field>
      </div>
      <Field label="E-mails des rapports" hint="Séparés par des virgules.">
        <input
          className={inputClass}
          value={f.reportEmails}
          onChange={set("reportEmails")}
          placeholder="gerant@exemple.fr, compta@exemple.fr"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ordre d'affichage">
          <input className={inputClass} type="number" min={0} value={f.position} onChange={set("position")} />
        </Field>
        <Field label="État">
          <label className="flex cursor-pointer items-center gap-2 py-[10px] text-[13px] font-semibold">
            <input
              type="checkbox"
              checked={f.active}
              onChange={(e) => setF({ ...f, active: e.target.checked })}
              className="h-4 w-4 accent-[var(--accent)]"
            />
            Boutique active
          </label>
        </Field>
      </div>
      {(save.error || del.error) && <ErrorState error={save.error ?? del.error} />}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        <Button onClick={() => save.mutate()} disabled={save.isPending || !f.city.trim() || !f.name.trim() || !f.hiboutikAccountId}>
          {save.isPending ? "Enregistrement…" : "Enregistrer"}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <div className="flex-1" />
        {boutique && (
          <Button
            variant="ghost"
            className="!text-danger hover:!bg-danger-bg"
            disabled={del.isPending}
            onClick={() => {
              if (window.confirm(`Supprimer définitivement la boutique ${boutique.city} et son historique dans Pilote ?`)) del.mutate();
            }}
          >
            Supprimer
          </Button>
        )}
      </div>
    </Drawer>
  );
}

// --- Groupes ------------------------------------------------------------------------------

function GroupDrawer({ group, boutiques, onClose }: { group: GroupDTO | null; boutiques: BoutiqueAdminDTO[]; onClose: () => void }) {
  const invalidate = useInvalidate();
  const [name, setName] = useState(group?.name ?? "");
  const [ids, setIds] = useState<string[]>(group?.boutiqueIds ?? []);
  const save = useMutation({
    mutationFn: () =>
      group
        ? api.put(`/groups/${group.id}`, { name: name.trim(), boutiqueIds: ids })
        : api.post("/groups", { name: name.trim(), boutiqueIds: ids }),
    onSuccess: () => {
      invalidate("groups");
      onClose();
    },
  });
  const del = useMutation({
    mutationFn: () => api.del(`/groups/${group!.id}`),
    onSuccess: () => {
      invalidate("groups");
      onClose();
    },
  });
  return (
    <Drawer open onClose={onClose} width={420}>
      <DrawerHeader title={group ? "Modifier le groupe" : "Nouveau groupe"} onClose={onClose} />
      <Field label="Nom du groupe">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. Grand Ouest" />
      </Field>
      <div>
        <FieldLabel>Boutiques ({ids.length})</FieldLabel>
        <div className="flex flex-col">
          {boutiques.map((b) => (
            <label
              key={b.id}
              className="flex cursor-pointer items-center gap-[10px] rounded-[10px] px-2 py-[7px] text-[13px] hover:bg-page"
            >
              <input
                type="checkbox"
                checked={ids.includes(b.id)}
                onChange={() => setIds((l) => (l.includes(b.id) ? l.filter((x) => x !== b.id) : [...l, b.id]))}
                className="h-4 w-4 accent-[var(--accent)]"
              />
              <span className="flex-1 font-semibold">{b.city}</span>
              <TypeBadge type={b.type} />
            </label>
          ))}
        </div>
      </div>
      {(save.error || del.error) && <ErrorState error={save.error ?? del.error} />}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>
          {save.isPending ? "Enregistrement…" : "Enregistrer"}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <div className="flex-1" />
        {group && (
          <Button
            variant="ghost"
            className="!text-danger hover:!bg-danger-bg"
            disabled={del.isPending}
            onClick={() => {
              if (window.confirm(`Supprimer le groupe « ${group.name} » ?`)) del.mutate();
            }}
          >
            Supprimer
          </Button>
        )}
      </div>
    </Drawer>
  );
}

// --- Page -------------------------------------------------------------------------------------

const COLS = "grid-cols-[1.3fr_100px_1fr_120px_110px_80px_1.1fr_90px]";

function SectionHead({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-0 flex-1">
        <div className="font-display text-[17px] font-bold">{title}</div>
        {sub && <div className="mt-[2px] text-[12.5px] text-muted">{sub}</div>}
      </div>
      {action}
    </div>
  );
}

/** Nom et logo du réseau courant (permission `network.customize`). */
function BrandingSection() {
  const { me } = useSession();
  const org = me.organization!;
  const { rename, uploadLogo, removeLogo } = useNetworkBranding();
  const [name, setName] = useState(org.name);
  const file = useRef<HTMLInputElement>(null);
  const error = rename.error ?? uploadLogo.error ?? removeLogo.error;
  const busy = uploadLogo.isPending || removeLogo.isPending;
  return (
    <section className="flex flex-col gap-[14px]">
      <SectionHead title="Personnalisation du réseau" sub="Le nom et le logo affichés en haut du menu, pour tous les membres du réseau." />
      <Card pad="px-[22px] py-5" className="flex flex-wrap items-start gap-6">
        <div className="flex items-center gap-4">
          <NetworkLogo name={name.trim() || org.name} logoUrl={org.logoUrl} size={64} tone="light" className="border border-line" />
          <div className="flex flex-col items-start gap-2">
            <Button variant="outline" onClick={() => file.current?.click()} disabled={busy}>
              {uploadLogo.isPending ? "Envoi…" : org.logoUrl ? "Changer le logo" : "Ajouter un logo"}
            </Button>
            {org.logoUrl && (
              <button
                type="button"
                disabled={busy}
                onClick={() => removeLogo.mutate()}
                className="text-[12.5px] font-semibold text-danger disabled:opacity-50"
              >
                Retirer le logo
              </button>
            )}
            <span className="text-[11.5px] text-muted-2">PNG, JPEG ou WebP, 1 Mo au plus. Carré de préférence.</span>
          </div>
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) uploadLogo.mutate(f);
            }}
          />
        </div>
        <form
          className="flex min-w-[260px] flex-1 flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && name.trim() !== org.name) rename.mutate(name.trim());
          }}
        >
          <FieldLabel>Nom du réseau</FieldLabel>
          <div className="flex gap-2">
            <input className={inputClass} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            <Button type="submit" disabled={rename.isPending || !name.trim() || name.trim() === org.name}>
              {rename.isPending ? "Enregistrement…" : "Renommer"}
            </Button>
          </div>
          {rename.isSuccess && name.trim() === org.name && <span className="text-[12px] font-semibold text-positive">Nom enregistré.</span>}
        </form>
        {error && <ErrorState error={error} className="w-full" />}
      </Card>
    </section>
  );
}

/** Copie dans le presse-papiers ; repli silencieux si le navigateur refuse. */
function CopyField({ label, value }: { label: string; value: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[11.5px] font-bold uppercase text-muted-2">{label}</div>
      <div className="flex gap-2">
        <input readOnly className={cx(inputClass, "tabular")} value={value} onFocus={(e) => e.currentTarget.select()} />
        <Button
          variant="outline"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => { setDone(true); setTimeout(() => setDone(false), 2000); }, () => undefined);
          }}
        >
          {done ? "Copié" : "Copier"}
        </Button>
      </div>
    </div>
  );
}

/** Assistants (Claude, ChatGPT…) : clés d'accès au MCP de la page IT (permission `network.customize`). */
function ItAssistants() {
  const { query, create, revoke } = useItApiKeys();
  const [name, setName] = useState("");
  const [canWrite, setCanWrite] = useState(true);
  const [fresh, setFresh] = useState<{ key: string; name: string } | null>(null);
  const [ask, setAsk] = useState<string | null>(null);
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate({ name: name.trim(), canWrite }, { onSuccess: (k) => { setFresh({ key: k.key, name: k.name }); setName(""); } });
  };
  return (
    <div className="flex flex-col gap-3 border-t border-line-soft pt-5">
      <div>
        <div className="text-[14px] font-semibold">Assistants (Claude, ChatGPT…)</div>
        <div className="text-[12.5px] text-muted">
          Une clé permet à un assistant de consulter, créer et chiffrer les projets et actions de ce réseau, uniquement ce réseau. Donnez une clé par assistant : vous pouvez la révoquer à tout moment.
        </div>
      </div>
      {fresh && (
        <div className="flex flex-col gap-3 rounded-card-sm bg-accent-soft px-4 py-3">
          <div className="text-[13px] font-semibold">Clé « {fresh.name} » créée. Copiez-la maintenant : elle ne sera plus affichée.</div>
          <CopyField label="Clé" value={fresh.key} />
          <CopyField label="Adresse du serveur" value={`${origin}/mcp`} />
          <div className="text-[12.5px] leading-[19px]">
            <b>Claude Code ou tout client qui accepte un en-tête :</b> adresse <span className="tabular">{origin}/mcp</span> et en-tête <span className="tabular">Authorization: Bearer &lt;la clé&gt;</span>.
            <br />
            <b>Claude.ai ou ChatGPT (connecteur personnalisé, sans authentification) :</b> collez l'adresse secrète ci-dessous. Elle contient la clé : ne la partagez pas, et révoquez-la si elle fuit.
          </div>
          <CopyField label="Adresse secrète (clé incluse)" value={`${origin}/mcp/${fresh.key}`} />
          <button type="button" className="self-start text-[12.5px] font-semibold text-accent" onClick={() => setFresh(null)}>J'ai copié, masquer</button>
        </div>
      )}
      {query.isLoading && <Loading />}
      {query.error && <ErrorState error={query.error} />}
      {(query.data ?? []).map((k) => (
        <div key={k.id} className="flex flex-wrap items-center gap-3 rounded-card-sm border border-line px-4 py-[10px]">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-semibold">{k.name}</div>
            <div className="text-[12px] text-muted">
              <span className="tabular">{k.prefix}…</span> · {k.canWrite ? "peut modifier" : "lecture seule"} · {k.lastUsedAt ? `utilisée ${relative(k.lastUsedAt)}` : "jamais utilisée"}
            </div>
          </div>
          {ask === k.id ? (
            <Button variant="danger" onClick={() => { setAsk(null); revoke.mutate(k.id); }}>Révoquer ?</Button>
          ) : (
            <button type="button" className="text-[12.5px] font-semibold text-danger" onClick={() => { setAsk(k.id); setTimeout(() => setAsk((a) => (a === k.id ? null : a)), 4000); }}>
              Révoquer
            </button>
          )}
        </div>
      ))}
      {query.data?.length === 0 && !fresh && <div className="text-[12.5px] text-muted">Aucune clé pour le moment.</div>}
      <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
        <div className="min-w-[200px] flex-1">
          <FieldLabel>Nouvelle clé</FieldLabel>
          <input className={inputClass} value={name} maxLength={60} placeholder="Ex. Claude de Ousmane" onChange={(e) => setName(e.target.value)} />
        </div>
        <label className="flex min-h-[40px] cursor-pointer items-center gap-2 text-[13px]">
          <input type="checkbox" className="h-4 w-4 accent-[var(--accent)]" checked={canWrite} onChange={(e) => setCanWrite(e.target.checked)} />
          Peut modifier
        </label>
        <Button type="submit" disabled={create.isPending || !name.trim()}>{create.isPending ? "Création…" : "Créer la clé"}</Button>
      </form>
      {(create.error || revoke.error) && <ErrorState error={create.error ?? revoke.error} />}
    </div>
  );
}

/** Page IT du réseau : l'activer, et enregistrer le jeton GitHub (permission `network.customize`). */
function ItSection() {
  const { query, save } = useItSettings();
  const [token, setToken] = useState("");
  const s = query.data;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = token.trim();
    if (t) save.mutate({ githubToken: t }, { onSuccess: () => setToken("") });
  };
  return (
    <section className="flex flex-col gap-[14px]">
      <SectionHead title="Page IT" sub="Suivi des livraisons du réseau : calendrier, actions à venir, temps estimé, et lecture automatique de GitHub." />
      <Card pad="px-[22px] py-5" className="flex flex-col gap-5">
        {query.isLoading && <Loading />}
        {query.error && <ErrorState error={query.error} />}
        {s && (
          <>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-[3px] h-[18px] w-[18px] accent-[var(--accent)]"
                checked={s.enabled}
                disabled={save.isPending}
                onChange={(e) => save.mutate({ enabled: e.target.checked })}
              />
              <span>
                <span className="block text-[14px] font-semibold">Activer la page IT pour ce réseau</span>
                <span className="block text-[12.5px] text-muted">Elle apparaît en bas du menu, pour les propriétaires et têtes de réseau.</span>
              </span>
            </label>
            <form className="flex max-w-[560px] flex-col gap-2" onSubmit={submit}>
              <FieldLabel>Jeton GitHub</FieldLabel>
              <div className="text-[12.5px] text-muted">
                {s.tokenSet ? (
                  <>
                    Un jeton est enregistré <span className="tabular">(••••{s.tokenHint})</span>. Il est chiffré en base et ne peut plus être relu. Collez-en un nouveau pour le remplacer.
                  </>
                ) : (
                  "Aucun jeton. Créez-le sur GitHub (Settings › Developer settings › Fine-grained tokens) avec la seule permission « Contents : lecture » sur vos dépôts."
                )}
              </div>
              <div className="flex gap-2">
                <input
                  className={inputClass}
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="github_pat_… ou ghp_…"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
                <Button type="submit" disabled={save.isPending || token.trim().length < 20}>
                  {save.isPending ? "Vérification…" : s.tokenSet ? "Remplacer" : "Enregistrer"}
                </Button>
              </div>
              {s.tokenSet && (
                <button type="button" disabled={save.isPending} onClick={() => save.mutate({ githubToken: null })} className="self-start text-[12.5px] font-semibold text-danger disabled:opacity-50">
                  Effacer le jeton
                </button>
              )}
              {save.isSuccess && !save.isPending && <span className="text-[12px] font-semibold text-positive">Enregistré.</span>}
            </form>
          </>
        )}
        {s?.enabled && <ItAssistants />}
        {save.error && <ErrorState error={save.error} />}
      </Card>
    </section>
  );
}

export function SettingsPage() {
  const { can, me } = useSession();
  const allowed = can("boutiques.manage");
  const accounts = useHiboutikAccounts();
  const boutiques = useAdminBoutiques(allowed);
  const groups = useGroups();
  const [accountEdit, setAccountEdit] = useState<HiboutikAccountDTO | null | "new">(null);
  const [boutiqueEdit, setBoutiqueEdit] = useState<BoutiqueAdminDTO | null | "new">(null);
  const [groupEdit, setGroupEdit] = useState<GroupDTO | null | "new">(null);

  if (!allowed) return <Empty>Vous n'avez pas accès aux paramètres du réseau.</Empty>;

  const accountLabel = (id: string) => accounts.data?.find((a) => a.id === id)?.label ?? "—";
  const cityOf = (id: string) => boutiques.data?.find((b) => b.id === id)?.city;

  return (
    <div className="flex flex-col gap-[26px]">
      {can("network.customize") && me.organization && <BrandingSection key={me.organization.id} />}
      {can("network.customize") && me.organization && <ItSection key={`it-${me.organization.id}`} />}
      {/* Comptes Hiboutik */}
      <section className="flex flex-col gap-[14px]">
        <SectionHead
          title="Comptes Hiboutik"
          sub="Un compte Hiboutik peut regrouper plusieurs boutiques (magasins). Les accès gérants peuvent être donnés par compte."
          action={<Button onClick={() => setAccountEdit("new")}>+ Ajouter un compte Hiboutik</Button>}
        />
        {accounts.isLoading && <Loading />}
        {accounts.error && <ErrorState error={accounts.error} />}
        {accounts.data?.length === 0 && (
          <Card className="text-center text-[13px] text-muted">Aucun compte Hiboutik. Ajoutez-en un pour connecter vos boutiques.</Card>
        )}
        <div className="grid gap-[14px] md:grid-cols-2 xl:grid-cols-3">
          {(accounts.data ?? []).map((a) => (
            <AccountCard key={a.id} a={a} onEdit={() => setAccountEdit(a)} />
          ))}
        </div>
      </section>

      {/* Boutiques */}
      <section className="flex flex-col gap-[14px]">
        <SectionHead
          title="Boutiques"
          sub="Rattachement Hiboutik, objectifs et rapports de chaque boutique."
          action={
            <Button onClick={() => setBoutiqueEdit("new")} disabled={!accounts.data?.length}>
              + Ajouter une boutique
            </Button>
          }
        />
        <div className="overflow-x-auto rounded-card border border-line bg-white">
          <div className="min-w-[980px]">
            <div className={cx("grid border-b border-line-soft px-5 py-3 text-[10.5px] font-bold uppercase text-muted-2", COLS)}>
              <span>Boutique</span>
              <span>Type</span>
              <span>Compte</span>
              <span>Magasin</span>
              <span>Objectif / mois</span>
              <span>État</span>
              <span>Synchro</span>
              <span />
            </div>
            {boutiques.isLoading && <Loading />}
            {boutiques.error && <ErrorState error={boutiques.error} className="m-5" />}
            {boutiques.data?.length === 0 && <Empty>Aucune boutique.</Empty>}
            {(boutiques.data ?? []).map((b) => (
              <div
                key={b.id}
                onClick={() => setBoutiqueEdit(b)}
                className={cx(
                  "grid cursor-pointer items-center border-b border-line-row px-5 py-[14px] text-[12.5px] last:border-b-0 hover:bg-[oklch(0.985_0.004_145)]",
                  COLS,
                )}
              >
                <div className="min-w-0 pr-2">
                  <div className="truncate text-[13px] font-semibold">{b.city}</div>
                  {b.name !== b.city && <div className="truncate text-[11.5px] text-muted-2">{b.name}</div>}
                </div>
                <span>
                  <TypeBadge type={b.type} />
                </span>
                <span className="truncate pr-2">{b.hiboutikAccountId ? accountLabel(b.hiboutikAccountId) : "Caisse Easy Shop"}</span>
                <span className="text-muted">
                  {b.shopId != null ? `#${b.shopId}` : "défaut"} · entr. {b.warehouseId}
                </span>
                <span className="tabular">{b.monthlyRevenueTarget != null ? eur(b.monthlyRevenueTarget) : "—"}</span>
                <span>
                  <Pill className={b.active ? "bg-positive-bg text-positive" : "bg-page text-muted-2"}>
                    {b.active ? "Active" : "Inactive"}
                  </Pill>
                </span>
                <span className="min-w-0 pr-2">
                  {b.lastSyncError ? (
                    <span className="block truncate font-semibold text-danger" title={b.lastSyncError}>
                      ⚠ {b.lastSyncError}
                    </span>
                  ) : (
                    <span className="text-muted">{b.lastSyncAt ? relative(b.lastSyncAt) : "jamais"}</span>
                  )}
                </span>
                <span className="text-right">
                  <span className="rounded-full border border-line px-[14px] py-[6px] text-[12px] font-bold text-accent">Modifier</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Groupes */}
      <section className="flex flex-col gap-[14px]">
        <SectionHead
          title="Groupes de boutiques"
          sub="Pour cibler rapidement messages, tâches et campagnes (ex. « Grand Ouest »)."
          action={<Button onClick={() => setGroupEdit("new")}>+ Nouveau groupe</Button>}
        />
        <Card pad="px-5 py-2">
          {groups.isLoading && <Loading />}
          {groups.error && <ErrorState error={groups.error} className="my-3" />}
          {groups.data?.length === 0 && <div className="py-6 text-center text-[13px] text-muted">Aucun groupe.</div>}
          {(groups.data ?? []).map((g) => (
            <div key={g.id} className="flex flex-wrap items-center gap-3 border-t border-line-soft py-3 first:border-t-0">
              <div className="min-w-[160px]">
                <div className="text-[13.5px] font-semibold">{g.name}</div>
                <div className="text-[11.5px] text-muted-2">
                  {g.boutiqueIds.length} boutique{g.boutiqueIds.length > 1 ? "s" : ""}
                </div>
              </div>
              <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                {g.boutiqueIds.map((id) => (
                  <Pill key={id} className="bg-page text-[oklch(0.4_0.02_150)]">
                    {cityOf(id) ?? "?"}
                  </Pill>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setGroupEdit(g)}
                className="rounded-full border border-line px-[14px] py-[6px] text-[12px] font-bold text-accent hover:bg-page"
              >
                Modifier
              </button>
            </div>
          ))}
        </Card>
      </section>

      {accountEdit && <AccountDrawer account={accountEdit === "new" ? null : accountEdit} onClose={() => setAccountEdit(null)} />}
      {boutiqueEdit && (
        <BoutiqueDrawer
          boutique={boutiqueEdit === "new" ? null : boutiqueEdit}
          accounts={accounts.data ?? []}
          onClose={() => setBoutiqueEdit(null)}
        />
      )}
      {groupEdit && (
        <GroupDrawer group={groupEdit === "new" ? null : groupEdit} boutiques={boutiques.data ?? []} onClose={() => setGroupEdit(null)} />
      )}
    </div>
  );
}
